import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Plus, Edit, Trash2, Eye, Search, Filter, Download, Upload, Copy, MessageSquare, Send, FileText, Zap, Code, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { sanitizeEmailContent } from "@/lib/sanitize";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import HtmlCodeEditor from "./HtmlCodeEditor";

// Template form schema
const templateFormSchema = z.object({
  name: z.string().min(1, "Template name is required"),
  subject: z.string().min(1, "Email subject is required"),
  bodyHtml: z.string().min(1, "HTML body is required"),
  bodyText: z.string().optional(),
  category: z.string().min(1, "Category is required"),
  variables: z.array(z.object({
    name: z.string(),
    type: z.string(),
    defaultValue: z.string().optional(),
    description: z.string().optional()
  })).optional()
});

type TemplateForm = z.infer<typeof templateFormSchema>;

interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  bodyHtml: string;
  bodyText?: string;
  category: string;
  variables?: any[];
  isActive: boolean;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  creatorName?: string;
}

interface SmsTemplate {
  id: number;
  name: string;
  templateKey: string;
  messageContent: string;
  description?: string | null;
  category: string;
  isActive: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export default function CRMTemplatesTab() {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<"email" | "sms">("email");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewVariables, setPreviewVariables] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [viewSource, setViewSource] = useState<'database' | 'aws'>('aws');

  // Fetch SMS templates (read-only view for CRM)
  const { data: smsTemplatesData, isLoading: smsTemplatesLoading } = useQuery<SmsTemplate[]>({
    queryKey: ['/api/sms-config/templates'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/sms-config/templates');
      return response.json();
    },
  });

  // Fetch templates from database
  const { data: templatesData, isLoading: templatesLoading, refetch: refetchTemplates } = useQuery({
    queryKey: ['/api/admin/crm/templates', currentPage, selectedCategory, searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);

      const response = await apiRequest('GET', `/api/admin/crm/templates?${params}`);
      return response.json();
    },
    enabled: viewSource === 'database'
  });

  // Fetch templates from AWS SES
  const { data: awsTemplatesData, isLoading: awsTemplatesLoading, refetch: refetchAwsTemplates } = useQuery({
    queryKey: ['/api/admin/crm/ses-templates'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/admin/crm/ses-templates');
      return response.json();
    },
    enabled: viewSource === 'aws'
  });

  // Normalize templates + pagination for both database and AWS SES views
  const pageSize = 20;

  // Reset to first page when switching between AWS and database views
  useEffect(() => {
    setCurrentPage(1);
  }, [viewSource]);

  let templates: EmailTemplate[] = [];
  let pagination: {
    page: number;
    totalPages: number;
    total: number;
    limit: number;
  } = {
    page: 1,
    totalPages: 1,
    total: 0,
    limit: pageSize
  };

  if (viewSource === 'aws') {
    const allAwsTemplates: EmailTemplate[] = (awsTemplatesData?.templates || []).map((t: any) => ({
      id: t.templateName,
      name: t.templateName,
      subject: t.subject,
      bodyHtml: t.htmlPart,
      bodyText: t.textPart,
      category: 'general',
      isActive: true,
      createdAt: t.createdTimestamp ? new Date(t.createdTimestamp).toISOString() : new Date().toISOString(),
      updatedAt: t.createdTimestamp ? new Date(t.createdTimestamp).toISOString() : new Date().toISOString(),
      createdBy: 0
    }));

    const total = allAwsTemplates.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const page = Math.min(currentPage, totalPages);
    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    templates = allAwsTemplates.slice(start, end);
    pagination = {
      page,
      totalPages,
      total,
      limit: pageSize
    };
  } else {
    templates = (templatesData?.templates || []) as EmailTemplate[];
    const dbPagination = templatesData?.pagination || {};
    pagination = {
      page: dbPagination.page || 1,
      totalPages: dbPagination.totalPages || 1,
      total: dbPagination.total || templates.length,
      limit: dbPagination.limit || pageSize
    };
  }

  const isLoading = viewSource === 'aws' ? awsTemplatesLoading : templatesLoading;

  // Create form
  const createForm = useForm<TemplateForm>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: {
      name: "",
      subject: "",
      bodyHtml: "",
      bodyText: "",
      category: "general",
      variables: []
    }
  });

  // Edit form
  const editForm = useForm<TemplateForm>({
    resolver: zodResolver(templateFormSchema)
  });

  // Create template mutation
  const createTemplateMutation = useMutation({
    mutationFn: async (data: TemplateForm) => {
      const response = await apiRequest('POST', '/api/admin/crm/templates', {
        body: data
      });
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Template created successfully!" });
      setIsCreateDialogOpen(false);
      createForm.reset();
      refetchTemplates();
    },
    onError: (error: any) => {
      toast({
        title: "Error creating template",
        description: error.message || "Failed to create template",
        variant: "destructive"
      });
    }
  });

  // Update template mutation
  const updateTemplateMutation = useMutation({
    mutationFn: async (data: TemplateForm & { id: number }) => {
      const response = await apiRequest('PATCH', `/api/admin/crm/templates/${data.id}`, {
        body: data
      });
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Template updated successfully!" });
      setIsEditDialogOpen(false);
      editForm.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/admin/crm/templates'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating template",
        description: error.message || "Failed to update template",
        variant: "destructive"
      });
    }
  });

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId: number) => {
      const response = await apiRequest('DELETE', `/api/admin/crm/templates/${templateId}`);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Template deleted successfully!" });
      refetchTemplates();
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting template",
        description: error.message || "Failed to delete template",
        variant: "destructive"
      });
    }
  });

  // Preview template mutation (database templates)
  const previewTemplateMutation = useMutation({
    mutationFn: async ({ templateId, variables }: { templateId: number; variables: Record<string, string> }) => {
      const response = await apiRequest('POST', `/api/admin/crm/templates/${templateId}/preview`, {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variables })
      });
      return response.json();
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setIsPreviewDialogOpen(true);
    },
    onError: (error: any) => {
      toast({
        title: "Error previewing template",
        description: error.message || "Failed to preview template",
        variant: "destructive"
      });
    }
  });

  // Preview AWS SES template mutation
  const previewAwsTemplateMutation = useMutation({
    mutationFn: async ({ templateName, variables }: { templateName: string; variables: Record<string, string> }) => {
      const response = await apiRequest('POST', `/api/admin/crm/ses-templates/${encodeURIComponent(templateName)}/preview`, {
        variables
      });
      return response.json();
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setIsPreviewDialogOpen(true);
    },
    onError: (error: any) => {
      toast({
        title: "Error previewing AWS template",
        description: error.message || "Failed to preview template from AWS SES",
        variant: "destructive"
      });
    }
  });

  const handleEdit = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    editForm.reset({
      name: template.name,
      subject: template.subject,
      bodyHtml: template.bodyHtml,
      bodyText: template.bodyText || "",
      category: template.category,
      variables: template.variables || []
    });
    setIsEditDialogOpen(true);
  };

  const handlePreview = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setPreviewVariables({});
    
    if (viewSource === 'aws') {
      // For AWS templates, use the template name
      previewAwsTemplateMutation.mutate({ templateName: template.name, variables: {} });
    } else {
      // For database templates, use the numeric ID
      previewTemplateMutation.mutate({ templateId: template.id, variables: {} });
    }
  };

  const categories = [
    { value: "all", label: "All Categories" },
    { value: "general", label: "General" },
    { value: "welcome", label: "Welcome" },
    { value: "onboarding", label: "Onboarding" },
    { value: "engagement", label: "Engagement" },
    { value: "tier_advancement", label: "Tier Advancement" },
    { value: "share_verification", label: "Share Verification" },
    { value: "promotional", label: "Promotional" },
    { value: "transactional", label: "Transactional" }
  ];

  // Sync templates mutation
  const syncTemplatesMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/admin/crm/sync-templates');
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Templates synced successfully!",
        description: `Added: ${data.added}, Updated: ${data.updated}, Skipped: ${data.skipped}`
      });
      refetchTemplates();
      refetchAwsTemplates();
    },
    onError: (error: any) => {
      toast({
        title: "Error syncing templates",
        description: error.message || "Failed to sync templates from AWS SES",
        variant: "destructive"
      });
    }
  });

  // Update AWS SES template mutation
  const updateAwsTemplateMutation = useMutation({
    mutationFn: async (data: { name: string; subject: string; bodyHtml: string; bodyText?: string }) => {
      const response = await apiRequest('PUT', `/api/admin/crm/ses-templates/${encodeURIComponent(data.name)}`, {
        subject: data.subject,
        bodyHtml: data.bodyHtml,
        bodyText: data.bodyText
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Template updated!",
        description: "AWS SES template has been updated successfully."
      });
      setIsEditDialogOpen(false);
      refetchAwsTemplates();
    },
    onError: (error: any) => {
      toast({
        title: "Error updating template",
        description: error.message || "Failed to update AWS SES template",
        variant: "destructive"
      });
    }
  });

  return (
    <div className="space-y-6">
      {/* Header with channel tabs */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="h-6 w-6" />
            Templates
          </h2>
          <p className="text-gray-600 mt-1">
            Manage templates for email, SMS, and in-app journeys
          </p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === "email" && (
            <>
              <Button
                variant={viewSource === 'aws' ? 'default' : 'outline'}
                onClick={() => setViewSource('aws')}
                data-testid="button-view-aws"
              >
                <Zap className="h-4 w-4 mr-2" />
                AWS SES
              </Button>
              <Button
                variant={viewSource === 'database' ? 'default' : 'outline'}
                onClick={() => setViewSource('database')}
                data-testid="button-view-database"
              >
                <FileText className="h-4 w-4 mr-2" />
                Database
              </Button>
              <Button
                variant="secondary"
                onClick={() => syncTemplatesMutation.mutate()}
                disabled={syncTemplatesMutation.isPending}
                data-testid="button-sync-templates"
              >
                <Download className="h-4 w-4 mr-2" />
                {syncTemplatesMutation.isPending ? 'Syncing...' : 'Sync from AWS'}
              </Button>
              <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="button-create-template">
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "email" | "sms")}>
        <TabsList className="mt-4">
          <TabsTrigger value="email">Email Templates</TabsTrigger>
          <TabsTrigger value="sms">SMS Templates</TabsTrigger>
        </TabsList>

        {/* Email templates tab */}
        <TabsContent value="email" className="space-y-6 mt-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Label htmlFor="search">Search Templates</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      id="search"
                      placeholder="Search by name or subject..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                      data-testid="input-search-templates"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger data-testid="select-template-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.value} value={category.value}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Email Templates Table */}
          <Card>
            <CardHeader>
              <CardTitle>
                {viewSource === 'aws' ? 'AWS SES Templates' : 'Database Templates'} ({templates.length})
              </CardTitle>
              <CardDescription>
                {viewSource === 'aws' 
                  ? 'View templates stored in your AWS SES account. Use "Sync from AWS" to import them to the database.'
                  : 'Manage your email templates stored in the database and preview how they look'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading templates...</p>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8">
                  <Mail className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
                  <p className="text-gray-500 mb-4">
                    {searchTerm || selectedCategory !== 'all'
                      ? "No templates match your current filters."
                      : "Get started by creating your first email template."
                    }
                  </p>
                  {(!searchTerm && selectedCategory === 'all') && (
                    <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="button-create-first-template">
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Template
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {templates.map((template: EmailTemplate) => (
                        <TableRow key={template.id} data-testid={`template-row-${template.id}`}>
                          <TableCell className="font-medium">{template.name}</TableCell>
                          <TableCell className="max-w-[300px] truncate">{template.subject}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {categories.find(c => c.value === template.category)?.label || template.category}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={template.isActive ? "default" : "secondary"}>
                              {template.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell>{new Date(template.createdAt).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePreview(template)}
                                data-testid={`button-preview-${template.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(template)}
                                data-testid={`button-edit-${template.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              {viewSource === 'database' && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      data-testid={`button-delete-${template.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete Template</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Are you sure you want to delete "{template.name}"? This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => deleteTemplateMutation.mutate(template.id)}
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-gray-500">
                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} templates
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={pagination.page <= 1}
                        >
                          Previous
                        </Button>
                        <span className="text-sm">
                          Page {pagination.page} of {pagination.totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages))}
                          disabled={pagination.page >= pagination.totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMS templates tab */}
        <TabsContent value="sms" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>SMS Templates</CardTitle>
              <CardDescription>
                View configured SMS templates available for journeys and campaigns. Manage full SMS settings from the{" "}
                <span className="font-medium">SMS &amp; OTP Config</span> section.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {smsTemplatesLoading ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  Loading SMS templates...
                </div>
              ) : !smsTemplatesData || smsTemplatesData.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  No SMS templates found. You can create SMS templates from the SMS &amp; OTP Config tab.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Key</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Usage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {smsTemplatesData.map((template) => (
                        <TableRow key={template.id}>
                          <TableCell>
                            <div className="font-medium">{template.name}</div>
                            {template.description && (
                              <div className="text-xs text-muted-foreground max-w-xs truncate">
                                {template.description}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <code className="text-xs">{template.templateKey}</code>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {template.category}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={template.isActive ? "default" : "outline"}>
                              {template.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right text-sm text-muted-foreground">
                            {template.usageCount ?? 0}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Template Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Email Template</DialogTitle>
            <DialogDescription>
              Create a new email template stored in AWS SES. Use {'{{variable}}'} syntax for dynamic content (e.g., {'{{firstName}}'}, {'{{email}}'}).
            </DialogDescription>
          </DialogHeader>
          <Form {...createForm}>
            <form onSubmit={createForm.handleSubmit((data) => createTemplateMutation.mutate(data))} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={createForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Template Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Welcome Email" {...field} data-testid="input-template-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={createForm.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger data-testid="select-new-template-category">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.slice(1).map((category) => (
                              <SelectItem key={category.value} value={category.value}>
                                {category.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={createForm.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Subject</FormLabel>
                    <FormControl>
                      <Input placeholder="Welcome to {{app_name}}!" {...field} data-testid="input-template-subject" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={createForm.control}
                name="bodyHtml"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>HTML Body</FormLabel>
                    <FormControl>
                      <HtmlCodeEditor
                        value={field.value}
                        onChange={field.onChange}
                        height="300px"
                        data-testid="textarea-template-html"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={createForm.control}
                name="bodyText"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Text Body (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Welcome {{first_name}}! Thank you for joining us..."
                        className="min-h-[100px]"
                        {...field}
                        data-testid="textarea-template-text"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createTemplateMutation.isPending}
                  data-testid="button-save-template"
                >
                  {createTemplateMutation.isPending ? "Creating..." : "Create Template"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
            <DialogDescription>
              Update the email template details
            </DialogDescription>
          </DialogHeader>
          <Form {...editForm}>
            <form onSubmit={editForm.handleSubmit((data) => {
              if (!selectedTemplate) return;
              if (viewSource === 'aws') {
                updateAwsTemplateMutation.mutate({
                  name: selectedTemplate.name,
                  subject: data.subject,
                  bodyHtml: data.bodyHtml,
                  bodyText: data.bodyText
                });
              } else {
                updateTemplateMutation.mutate({ ...data, id: selectedTemplate.id });
              }
            })} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={editForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Template Name</FormLabel>
                      <FormControl>
                        <Input {...field} readOnly disabled className="bg-gray-100 cursor-not-allowed" data-testid="input-edit-template-name" />
                      </FormControl>
                      <p className="text-xs text-gray-500 mt-1">Template name cannot be changed once created</p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger data-testid="select-edit-template-category">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.slice(1).map((category) => (
                              <SelectItem key={category.value} value={category.value}>
                                {category.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={editForm.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Subject</FormLabel>
                    <FormControl>
                      <Input {...field} data-testid="input-edit-template-subject" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="bodyHtml"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>HTML Body</FormLabel>
                    <FormControl>
                      <HtmlCodeEditor
                        value={field.value}
                        onChange={field.onChange}
                        height="300px"
                        data-testid="textarea-edit-template-html"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="bodyText"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Text Body (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        className="min-h-[100px]"
                        {...field}
                        data-testid="textarea-edit-template-text"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateTemplateMutation.isPending || updateAwsTemplateMutation.isPending}
                  data-testid="button-update-template"
                >
                  {(updateTemplateMutation.isPending || updateAwsTemplateMutation.isPending) 
                    ? "Updating..." 
                    : viewSource === 'aws' 
                      ? "Update AWS Template" 
                      : "Update Template"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog with Edit Capability */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Template Preview {selectedTemplate && `- ${selectedTemplate.name}`}
            </DialogTitle>
            <DialogDescription>
              Preview the rendered HTML or edit the template code directly
            </DialogDescription>
          </DialogHeader>
          {previewData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-sm font-medium">Subject</Label>
                  <p className="text-sm text-gray-700">{previewData.subject}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Template Variables</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(previewData.extractedVariables || []).length > 0 ? (
                      (previewData.extractedVariables || []).map((v: string) => (
                        <Badge key={v} variant="outline" className="text-xs">
                          {`{{${v}}}`}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">No variables</span>
                    )}
                  </div>
                </div>
              </div>

              <Tabs defaultValue="preview" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="preview" className="flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    HTML Preview
                  </TabsTrigger>
                  <TabsTrigger value="code" className="flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    Edit Code
                  </TabsTrigger>
                  <TabsTrigger value="text" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Text Version
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="preview" className="mt-4">
                  <div className="border rounded-lg bg-white overflow-hidden">
                    <div className="bg-gray-100 px-4 py-2 border-b text-xs text-gray-500 flex items-center gap-2">
                      <Mail className="h-3 w-3" />
                      Rendered Email Preview
                    </div>
                    <div className="p-6 min-h-[400px] max-h-[500px] overflow-auto">
                      <div dangerouslySetInnerHTML={{ __html: sanitizeEmailContent(previewData.htmlBody) }} />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="code" className="mt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">HTML Source Code</Label>
                    {viewSource === 'aws' && selectedTemplate && (
                      <Button
                        size="sm"
                        onClick={() => {
                          if (selectedTemplate) {
                            updateAwsTemplateMutation.mutate({
                              name: selectedTemplate.name,
                              subject: previewData.subject,
                              bodyHtml: previewData.htmlBody,
                              bodyText: previewData.textBody
                            });
                          }
                        }}
                        disabled={updateAwsTemplateMutation.isPending}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {updateAwsTemplateMutation.isPending ? "Saving..." : "Save to AWS"}
                      </Button>
                    )}
                  </div>
                  <HtmlCodeEditor
                    value={previewData.htmlBody || ""}
                    onChange={(value) => setPreviewData({ ...previewData, htmlBody: value })}
                    height="450px"
                  />
                </TabsContent>

                <TabsContent value="text" className="mt-4">
                  <div className="border rounded-lg bg-gray-50 overflow-hidden">
                    <div className="bg-gray-100 px-4 py-2 border-b text-xs text-gray-500">
                      Plain Text Version (for email clients that don't support HTML)
                    </div>
                    <div className="p-4 min-h-[400px] max-h-[500px] overflow-auto">
                      <pre className="whitespace-pre-wrap text-sm font-mono">
                        {previewData.textBody || "No text version available"}
                      </pre>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
